/**
 * 手机 GPS 多为 WGS-84；国内 Google 底图与道路为 GCJ-02，直接叠 WGS 会偏数百米。
 * 仅用于地图展示；存储与表格仍用原始 WGS-84。
 */

const PI = Math.PI;
const A = 6378245.0;
const EE = 0.00669342162296594323;

/**
 * 是否不在「中国大陆 GCJ 纠偏」范围内（境外或框外）。
 * 为 true 时地图应直接使用 WGS-84，不做火星坐标转换。
 */
export function isOutsideChinaGcjOffsetZone(lat: number, lng: number): boolean {
  return lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271;
}

function transformLat(lng: number, lat: number): number {
  let ret =
    -100.0 +
    2.0 * lng +
    3.0 * lat +
    0.2 * lat * lat +
    0.1 * lng * lat +
    0.2 * Math.sqrt(Math.abs(lng));
  ret += ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(lat * PI) + 40.0 * Math.sin((lat / 3.0) * PI)) * 2.0) / 3.0;
  ret +=
    ((160.0 * Math.sin((lat / 12.0) * PI) + 320 * Math.sin((lat * PI) / 30.0)) * 2.0) / 3.0;
  return ret;
}

function transformLng(lng: number, lat: number): number {
  let ret =
    300.0 +
    lng +
    2.0 * lat +
    0.1 * lng * lng +
    0.1 * lng * lat +
    0.1 * Math.sqrt(Math.abs(lng));
  ret += ((20.0 * Math.sin(6.0 * lng * PI) + 20.0 * Math.sin(2.0 * lng * PI)) * 2.0) / 3.0;
  ret += ((20.0 * Math.sin(lng * PI) + 40.0 * Math.sin((lng / 3.0) * PI)) * 2.0) / 3.0;
  ret +=
    ((150.0 * Math.sin((lng / 12.0) * PI) + 300.0 * Math.sin((lng / 30.0) * PI)) * 2.0) / 3.0;
  return ret;
}

/** 仅在中国大陆框内调用：WGS-84 → GCJ-02 */
function wgs84ToGcj02InZone(wgsLat: number, wgsLng: number): { lat: number; lng: number } {
  let dLat = transformLat(wgsLng - 105.0, wgsLat - 35.0);
  let dLng = transformLng(wgsLng - 105.0, wgsLat - 35.0);
  const radLat = (wgsLat / 180.0) * PI;
  let magic = Math.sin(radLat);
  magic = 1 - EE * magic * magic;
  const sqrtMagic = Math.sqrt(magic);
  dLat = (dLat * 180.0) / (((A * (1 - EE)) / (magic * sqrtMagic)) * PI);
  dLng = (dLng * 180.0) / ((A / sqrtMagic) * Math.cos(radLat) * PI);
  return {
    lat: wgsLat + dLat,
    lng: wgsLng + dLng,
  };
}

/**
 * 地图展示用经纬度：国外/框外直接返回 WGS；仅国内走 GCJ 转换。
 */
export function wgsToMapDisplayLatLng(wgsLat: number, wgsLng: number): { lat: number; lng: number } {
  if (isOutsideChinaGcjOffsetZone(wgsLat, wgsLng)) {
    return { lat: wgsLat, lng: wgsLng };
  }
  return wgs84ToGcj02InZone(wgsLat, wgsLng);
}
